import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { Cookie, Settings, BarChart, Users, Shield, Clock } from 'lucide-react';

function CookiePolicy() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const styles = `
        .cookie-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%);
            padding: 2rem 1rem;
        }

        .cookie-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 24px;
            padding: 2.5rem;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(6, 182, 212, 0.15);
        }

        .cookie-title {
            font-size: 2.5rem;
            font-weight: 800;
            background: linear-gradient(90deg, #6366f1, #06b6d4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
        }

        .last-updated {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 2rem;
        }

        .section-title {
            color: #0e4aa5;
            font-weight: 700;
            font-size: 1.3rem;
            margin: 1.5rem 0 1rem 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .section-icon {
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #6366f1, #06b6d4);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }

        .cookie-text {
            color: #444;
            line-height: 1.8;
            font-size: 1rem;
            margin-bottom: 1rem;
            text-align: left;
            font-weight: bold;
        }

        .cookie-list {
            list-style: none;
            padding-left: 0;
            margin-left: 0;
            text-align: left;
        }

        .cookie-list li {
            color: #555;
            line-height: 1.8;
            margin-bottom: 0.5rem;
            position: relative;
            padding-left: 1.8rem;
            text-align: left;
        }

        .cookie-list li::before {
            content: "•";
            color: #06b6d4;
            font-weight: bold;
            position: absolute;
            left: 0.4rem;
        }

        .cookie-table {
            width: 100%;
            border-collapse: collapse;
            margin: 1rem 0;
        }

        .cookie-table th {
            background: linear-gradient(135deg, #6366f1, #06b6d4);
            color: white;
            padding: 12px;
            text-align: left;
            font-weight: 600;
        }

        .cookie-table td {
            padding: 12px;
            border-bottom: 1px solid rgba(6, 182, 212, 0.2);
            color: #555;
        }

        .cookie-table tr:hover {
            background: rgba(6, 182, 212, 0.05);
        }

        @media (max-width: 768px) {
            .cookie-card {
                padding: 1.5rem;
            }
            .cookie-title {
                font-size: 2rem;
            }
            .section-title {
                font-size: 1.2rem;
            }
            .cookie-table {
                font-size: 0.85rem;
            }
            .cookie-table th, .cookie-table td {
                padding: 8px;
            }
        }
    `;

    return (
        <div className="cookie-container">
            <style>{styles}</style>
            <div className="container" style={{ maxWidth: 900 }}>
                <button
                    className="btn btn-outline-primary d-flex align-items-center mb-4"
                    onClick={() => navigate('/')}
                    style={{
                        borderRadius: '12px',
                        padding: '10px 20px',
                        borderWidth: '2px',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0d53bd';
                        e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#0d53bd';
                    }}
                >
                    <FaCircleArrowLeft className="me-2" size={18} />
                    Back to Home
                </button>

                <div className="cookie-card">
                    <h1 className="cookie-title">Cookie Policy</h1>
                    <p className="last-updated">Last Updated: April 20, 2026</p>

                    <p className="cookie-text">
                        This Cookie Policy explains how InsightDraft AI uses cookies and similar technologies 
                        to recognize you when you visit our platform. It explains what these technologies are 
                        and why we use them.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Cookie size={18} /></span>
                        What Are Cookies?
                    </div>
                    <p className="cookie-text">
                        Cookies are small text files that are placed on your device when you visit a website. 
                        They are widely used to make websites work more efficiently and provide information 
                        to website owners.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Settings size={18} /></span>
                        Types of Cookies We Use
                    </div>
                    <p className="cookie-text">
                        We use the following types of cookies:
                    </p>

                    <table className="cookie-table">
                        <thead>
                            <tr>
                                <th>Cookie Type</th>
                                <th>Purpose</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Essential Cookies</strong></td>
                                <td>Required for basic site functionality (login, security)</td>
                                <td>Session</td>
                            </tr>
                            <tr>
                                <td><strong>Functional Cookies</strong></td>
                                <td>Remember your preferences and settings</td>
                                <td>1 year</td>
                            </tr>
                            <tr>
                                <td><strong>Analytics Cookies</strong></td>
                                <td>Track site usage and performance</td>
                                <td>2 years</td>
                            </tr>
                            <tr>
                                <td><strong>Marketing Cookies</strong></td>
                                <td>Deliver relevant advertisements</td>
                                <td>90 days</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section-title">
                        <span className="section-icon"><BarChart size={18} /></span>
                        Analytics Cookies
                    </div>
                    <p className="cookie-text">
                        We use Google Analytics to understand how visitors interact with our platform. 
                        These cookies collect information about:
                    </p>
                    <ul className="cookie-list">
                        <li>Pages visited and time spent on site</li>
                        <li>Geographic location (country/city level only)</li>
                        <li>Device and browser information</li>
                        <li>Referral sources</li>
                    </ul>
                    <p className="cookie-text">
                        All data collected is anonymized and cannot identify you personally.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Users size={18} /></span>
                        Third-Party Cookies
                    </div>
                    <p className="cookie-text">
                        Some cookies are placed by third-party services we use:
                    </p>
                    <ul className="cookie-list">
                        <li><strong>Google Analytics:</strong> Website analytics</li>
                        <li><strong>Stripe:</strong> Payment processing (if applicable)</li>
                        <li><strong>Intercom:</strong> Customer support chat</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><Shield size={18} /></span>
                        Managing Cookies
                    </div>
                    <p className="cookie-text">
                        You can control and manage cookies through:
                    </p>
                    <ul className="cookie-list">
                        <li><strong>Browser Settings:</strong> Block or delete cookies via browser preferences</li>
                        <li><strong>Opt-Out Tools:</strong> Use Google Analytics Opt-out Browser Add-on</li>
                        <li><strong>Do Not Track:</strong> Enable "Do Not Track" in your browser</li>
                    </ul>
                    <p className="cookie-text">
                        Note: Blocking essential cookies may affect site functionality.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Clock size={18} /></span>
                        Cookie Duration
                    </div>
                    <p className="cookie-text">
                        Cookies remain on your device for varying periods:
                    </p>
                    <ul className="cookie-list">
                        <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                        <li><strong>Persistent Cookies:</strong> Remain until they expire or you delete them</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><Cookie size={18} /></span>
                        Updates to This Policy
                    </div>
                    <p className="cookie-text">
                        We may update this Cookie Policy periodically. The updated version will be indicated 
                        by the "Last Updated" date. Continued use of our platform constitutes acceptance of changes.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Shield size={18} /></span>
                        Contact Us
                    </div>
                    <p className="cookie-text">
                        For questions about our Cookie Policy, contact:<br />
                        <strong>Email:</strong> c84289798@gmail.com<br />
                        <strong>Location:</strong> Ahmedabad, India
                    </p>
                </div>
            </div>
        </div>
    );
}

export default CookiePolicy;